import React, { useState } from 'react';
import { styled, Theme, CSSObject, useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { 
  Box, 
  Drawer as MuiDrawer, 
  AppBar as MuiAppBar, 
  Toolbar, 
  List, 
  Typography, 
  Divider, 
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Badge
} from '@mui/material';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  MonetizationOn as CoinIcon,
  Description as ReportsIcon,
  HelpOutline as HelpIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SupportButton from '../support/SupportButton';
import logoImage from '../../assets/carconsul-high-resolution-logo-grayscale-transparent.png';

const drawerWidth = 280;

// Styles for the drawer when open
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  borderRight: `1px solid ${theme.palette.divider}`,
});

// Styles for the drawer when closed
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  borderRight: `1px solid ${theme.palette.divider}`,
});

// App bar styles
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// Drawer styles
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

// Custom styles
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile); // Start closed on mobile
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
    handleClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    // Auto-close drawer on mobile after navigation
    if (isMobile) {
      setOpen(false);
      handleMobileMenuClose();
    }
  };

  const handleMobileMenuNavigate = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const handleMobileLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
    handleMobileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile Layout */}
      {isMobile ? (
        <>
          <AppBar position="fixed" elevation={0}>
            <Toolbar sx={{ minHeight: { xs: 56 } }}>
              {/* Mobile Menu Button */}
              <IconButton
                color="inherit"
                aria-label="open menu"
                onClick={handleMobileMenuOpen}
                edge="start"
                sx={{ mr: 1 }}
              >
                <MoreVertIcon />
              </IconButton>

              {/* Logo */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexGrow: 1,
                  cursor: 'pointer'
                }}
                onClick={() => handleNavigate('/')}
              >
                <Box 
                  component="img"
                  src={logoImage}
                  alt="CarConsul Logo"
                  sx={{ 
                    height: 48,
                    mr: 1,
                    filter: 'brightness(1.2)'
                  }}
                />
                <Typography 
                  variant="subtitle1" 
                  noWrap 
                  component="div"
                  sx={{ 
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Assistant
                </Typography>
              </Box>

              {/* New Chat Button - Circular */}
              <IconButton
                color="primary"
                onClick={() => handleNavigate('/new-chat')}
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Mobile Dropdown Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            PaperProps={{
              sx: {
                width: 280,
                maxHeight: '80vh'
              }
            }}
          >
            {/* User Info */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar 
                  alt={userProfile?.nickname || 'User'} 
                  src="/static/images/avatar/1.jpg"
                  sx={{ width: 32, height: 32, mr: 1.5 }}
                >
                  {userProfile?.nickname?.[0] || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {userProfile?.nickname || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userProfile?.email || ''}
                  </Typography>
                </Box>
              </Box>
              
              {/* Coin Balance */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CoinIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: '1rem' }} />
                <Typography variant="body2">
                  {userProfile?.coinBalance || 0} Coins
                </Typography>
              </Box>
            </Box>

            {/* Navigation Items */}
            <MenuItem onClick={() => handleMobileMenuNavigate('/chats')}>
              <ListItemIcon>
                <ChatIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="All Chats" />
            </MenuItem>

            <MenuItem onClick={() => handleMobileMenuNavigate('/reports')}>
              <ListItemIcon>
                <ReportsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="My Reports" />
            </MenuItem>

            <MenuItem onClick={() => handleMobileMenuNavigate('/coins')}>
              <ListItemIcon>
                <CoinIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Buy Coins" />
            </MenuItem>

            <Divider />

            <MenuItem onClick={() => handleMobileMenuNavigate('/profile')}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </MenuItem>

            <MenuItem onClick={() => handleMobileMenuNavigate('/settings')}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>

            <MenuItem onClick={handleMobileMenuClose} sx={{ px: 0 }}>
              <ListItemIcon>
                <HelpIcon fontSize="small" />
              </ListItemIcon>
              <SupportButton
                context="general"
                variant="text"
                fullWidth
                label="Get Support"
                showIcon={false}
                size="small"
                sx={{ 
                  justifyContent: 'flex-start',
                  pl: 0,
                  ml: 0,
                  textTransform: 'none'
                }}
              />
            </MenuItem>

            {userProfile?.isAdmin && (
              <MenuItem onClick={() => handleMobileMenuNavigate('/admin')}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Admin Dashboard" />
              </MenuItem>
            )}

            <Divider />

            <MenuItem onClick={handleMobileLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>

          {/* Main Content */}
          <Box component="main" sx={{ 
            flexGrow: 1, 
            p: 1,
            pt: 8, // Account for fixed AppBar
            minHeight: '100vh',
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden'
          }}>
            {children}
          </Box>
        </>
      ) : (
        /* Desktop Layout */
        <>
          <AppBar position="fixed" open={open} elevation={0}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  marginRight: 5,
                  ...(open && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1,
              cursor: 'pointer'
            }}
            onClick={() => handleNavigate('/')}
          >
            <Box 
              component="img"
              src={logoImage}
              alt="CarConsul Logo"
              sx={{ 
                height: isMobile ? 48 : 64,
                mr: isMobile ? 1 : 2,
                filter: 'brightness(1.2)'
              }}
            />
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              noWrap 
              component="div"
              sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: isMobile ? { xs: 'none', sm: 'block' } : 'block'
              }}
            >
              Assistant
            </Typography>
          </Box>
          
          {/* Coins display */}
          <Tooltip title="Coin Balance">
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mr: isMobile ? 1 : 2,
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
              onClick={() => handleNavigate('/coins')}
            >
              <CoinIcon sx={{ 
                color: 'inherit', 
                mr: 0.5,
                fontSize: isMobile ? '1rem' : '1.25rem',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { 
                    transform: 'scale(1)',
                    opacity: 1 
                  },
                  '50%': { 
                    transform: 'scale(1.1)',
                    opacity: 0.8 
                  }
                }
              }} />
              <Typography variant={isMobile ? "caption" : "body2"}>
                {userProfile?.coinBalance || 0} {isMobile ? '' : 'Coins'}
              </Typography>
            </Box>
          </Tooltip>

          {/* Notifications */}
          <IconButton
            size={isMobile ? "medium" : "large"}
            aria-label="show notifications"
            color="inherit"
            sx={{ mr: isMobile ? 1 : 2 }}
          >
            <Badge badgeContent={0} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User menu */}
          <Box>
            <Tooltip title="Account settings">
              <IconButton
                size={isMobile ? "medium" : "large"}
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ p: 0 }}
              >
                <Avatar alt={userProfile?.nickname || 'User'} src="/static/images/avatar/1.jpg" />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </MenuItem>
              <MenuItem onClick={handleSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          {open && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexGrow: 1,
                mr: 1,
                cursor: 'pointer'
              }}
              onClick={() => handleNavigate('/')}
            >
              <Box 
                component="img"
                src={logoImage}
                alt="CarConsul Logo"
                sx={{ 
                  height: 48,
                  mr: 1,
                  filter: 'brightness(1.2)'
                }}
              />
            </Box>
          )}
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* New Chat Button */}
          <Box sx={{ px: 2, mb: 2 }}>
            <IconButton
              color="primary"
              aria-label="new chat"
              onClick={() => handleNavigate('/new-chat')}
              sx={{ 
                width: '100%', 
                borderRadius: 2, 
                p: 1,
                border: '1px solid',
                borderColor: 'primary.main',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: (theme) => theme.shadows[4],
                },
                '&:active': {
                  transform: 'translateY(0px)',
                }
              }}
            >
              <AddIcon sx={{ 
                mr: 1, 
                transition: 'transform 0.2s ease-in-out',
                fontSize: isMobile ? '1.2rem' : '1.5rem'
              }} />
              {open && <Typography variant="button">New Chat</Typography>}
            </IconButton>
          </Box>

          <List>
            {/* All Chats */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateX(4px)',
                  },
                }}
                onClick={() => handleNavigate('/chats')}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <ChatIcon sx={{ 
                    transition: 'transform 0.2s ease-in-out',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="All Chats" 
                  sx={{ 
                    opacity: open ? 1 : 0,
                    '& .MuiListItemText-primary': {
                      fontWeight: 500,
                      fontSize: '0.95rem'
                    }
                  }} 
                />
              </ListItemButton>
            </ListItem>

            {/* My Reports */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateX(4px)',
                  },
                }}
                onClick={() => handleNavigate('/reports')}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <ReportsIcon sx={{ 
                    transition: 'transform 0.2s ease-in-out',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="My Reports" 
                  sx={{ 
                    opacity: open ? 1 : 0,
                    '& .MuiListItemText-primary': {
                      fontWeight: 500,
                      fontSize: '0.95rem'
                    }
                  }} 
                />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />
          
          {/* Coins section */}
          <List>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateX(4px)',
                  },
                }}
                onClick={() => handleNavigate('/coins')}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <CoinIcon sx={{ 
                    color: 'inherit',
                    fontSize: isMobile ? '1.1rem' : '1.5rem',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1) rotate(10deg)',
                    }
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Buy Coins" 
                  sx={{ 
                    opacity: open ? 1 : 0,
                    '& .MuiListItemText-primary': {
                      fontWeight: 500,
                      fontSize: '0.95rem'
                    }
                  }} 
                />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />
          
          {/* Support section */}
          <Box sx={{ px: 2, mb: 2 }}>
            <SupportButton
              context="general"
              variant="outlined"
              fullWidth
              label={open ? "Get Support" : ""}
              showIcon={true}
              size="medium"
            />
          </Box>

          {/* Admin section */}
          {userProfile?.isAdmin && (
            <>
              <Divider sx={{ my: 2 }} />
              <List>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'translateX(4px)',
                      },
                    }}
                    onClick={() => handleNavigate('/admin')}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <AdminIcon sx={{ 
                        transition: 'transform 0.2s ease-in-out',
                        fontSize: isMobile ? '1.1rem' : '1.5rem',
                        color: 'warning.main',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        }
                      }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Admin Dashboard" 
                      sx={{ 
                        opacity: open ? 1 : 0,
                        '& .MuiListItemText-primary': {
                          fontWeight: 500,
                          fontSize: '0.95rem'
                        }
                      }} 
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </>
          )}
          
          {/* Spacer to push account to bottom */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* User info at bottom */}
          {open && (
            <Box 
              sx={{ 
                p: 2, 
                borderTop: 1, 
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(16, 185, 129, 0.1))'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  alt={userProfile?.nickname || 'User'} 
                  src="/static/images/avatar/1.jpg"
                  sx={{ 
                    width: 36, 
                    height: 36, 
                    mr: 1.5,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  {userProfile?.nickname?.[0] || 'U'}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                    {userProfile?.nickname || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {userProfile?.email || ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          </Box>
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <DrawerHeader />
          {children}
        </Box>
      </>
      )}
    </Box>
  );
};

export default AppLayout;
